import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldTypesEnum } from 'constants/loanActionEditor';
import { deleteByIndex, setIn } from 'utils/data';
import { findPriorToOption, getPriorToPickerOptions } from 'utils/priorTo';

import { selectFormsInfoListOptions } from 'store/forms/selectors';
import { selectLoanActionsConditions } from 'store/system/selectors';
import { selectPriorTo } from 'store/underwriting/selectors';

import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import { wrapperClassname } from './LoanActionItem.constants';
import { DynamicValueInput } from './LoanActionItems';

const LoanActionConditionRow = ({
  index,
  rootPath,
  isDisabled,
  isEditAllowed,
  options,
  isSingleItem,
}) => {
  const priorToOptions = useSelector(selectPriorTo);
  const formsOptions = useSelector(selectFormsInfoListOptions);
  const conditions = useSelector(selectLoanActionsConditions);
  const { setInForm, setMultipleInForm, form, setValidationErrors } = useFormContext();

  const fieldPath = `${rootPath}[${index}]`;
  const conditionIdPath = `${fieldPath}.conditionId`;
  const priorToTypePath = `${fieldPath}.priorToType`;
  const borrowerMessagePath = `${fieldPath}.borrowerMessage`;
  const numberPath = `${fieldPath}.number`;
  const taskTypesPath = `${fieldPath}.taskTypes`;
  const formConditions = _.get(form, rootPath);
  const formTaskTypes = _.get(form, taskTypesPath);
  const formPriorToType = _.get(form, priorToTypePath);

  const priorToPickerOptions = useMemo(
    () => getPriorToPickerOptions({ options: priorToOptions, currentOption: formPriorToType }),
    [priorToOptions, formPriorToType],
  );

  const onDeleteRow = useCallback(() => {
    const newForms = [...formConditions];
    newForms.splice(index, 1);
    setInForm(rootPath, newForms);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [formConditions, index, setInForm, rootPath, setValidationErrors]);

  const onChange = useCallback(
    (_, { value: conditionId }) => {
      const { priorToType, priorToTypeId, borrowerMessage, number, taskTypes } =
        conditions[conditionId] || {};

      setMultipleInForm({
        [priorToTypePath]: findPriorToOption({
          options: priorToOptions,
          priorToTypeId,
          priorToType,
        }),
        [borrowerMessagePath]: borrowerMessage,
        [numberPath]: number,
        [taskTypesPath]: taskTypes,
      });
    },
    [
      borrowerMessagePath,
      conditions,
      numberPath,
      priorToOptions,
      priorToTypePath,
      setMultipleInForm,
      taskTypesPath,
    ],
  );

  return (
    <div className={wrapperClassname}>
      <div className="flex">
        <DynamicValueInput
          required
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          label="Condition"
          name={conditionIdPath}
          options={options}
          type={FieldTypesEnum.SELECT}
          withSortedOptions={false}
          onChange={onChange}
        />
        {isEditAllowed && !isSingleItem && (
          <ButtonWithTooltip
            data-testid={loanManagementTestIds.getDeleteRowButtonId(fieldPath)}
            disabled={isDisabled}
            icon={DeleteIcon}
            size="medium"
            spanContainerClassName="flex items-center"
            title="Delete row"
            onClick={onDeleteRow}
          />
        )}
      </div>
      <DynamicValueInput
        required
        disabled={isDisabled || !isEditAllowed}
        label="Prior To Type"
        name={priorToTypePath}
        options={priorToPickerOptions}
        type={FieldTypesEnum.SELECT}
        withSortedOptions={false}
      />
      <DynamicValueInput
        className="w-full"
        disabled={isDisabled || !isEditAllowed}
        label="Borrower Facing Condition"
        name={borrowerMessagePath}
        type={FieldTypesEnum.STRING}
      />
      {formTaskTypes?.map((taskType, index) => {
        if (!taskType.isFormRequired) {
          return null;
        }

        const taskTypePath = `${taskTypesPath}[${index}].taskTypeName`;
        const formBundlePath = `${taskTypesPath}[${index}].formBundleId`;

        return (
          <div key={index}>
            <DynamicValueInput
              disabled
              className="w-full"
              label="Task Name"
              name={taskTypePath}
              type={FieldTypesEnum.STRING}
            />
            <DynamicValueInput
              required
              disabled={isDisabled || !isEditAllowed}
              label="Form"
              name={formBundlePath}
              options={formsOptions}
              type={FieldTypesEnum.SELECT}
            />
          </div>
        );
      })}
    </div>
  );
};

LoanActionConditionRow.propTypes = {
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  options: PropTypes.array.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isSingleItem: PropTypes.bool.isRequired,
};

export default memo(LoanActionConditionRow);
